import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Stack, alpha } from "@mui/material";
import {
  Notifications as NotificationsIcon,
  History as HistoryIcon,
} from "@mui/icons-material";

import ActionButton from "../../atoms/ActionButton";
import SelectableListRow from "../../molecules/SelectableListRow";
import { RowLine, RowChip, RowMetaText } from "./_rowParts";
import ConfirmDialog from "../../common/ConfirmDialog";
import NotificationEditor from "./NotificationEditor";
import NotificationLogDrawer from "./drawers/NotificationLogDrawer";
import {
  LIST_ROW_SX,
  WORKSPACE_ROOT_SX,
  WorkspaceBody,
  WorkspaceSplit,
  WorkspaceSkeleton,
  WorkspaceEmpty,
  SelectPrompt,
  CreatingRow,
} from "./_workspaceParts";
import { useClinicTheme } from "../../../theme";
import { COLORS } from "../../../styles/uiConstants";
import {
  useListNotificationSettingsByPatientMutation,
  useListNotificationLogsByPatientMutation,
  useDeleteNotificationSettingMutation,
  useSendNotificationNowMutation,
} from "../../../store/secretaryApi";
import useTerms from "../../../hooks/useTerms";

const FREQUENCY_LABELS = {
  once: "Bir kez",
  daily: "Günlük",
  weekly: "Haftalık",
  monthly: "Aylık",
};

const RESPONSE_LABELS = {
  yes_no: "Evet / Hayır cevaplı",
  multiple_choice: "Çoktan seçmeli",
  free_text: "Yazılı cevaplı",
};

/** Hasta çalışma alanı "Uygulama Bildirimleri" — düz liste | editör (§8.3). */
export default function NotificationTab({
  mode,
  patientId,
  token,
  canEdit = true,
  initialData,
}) {
  const terms = useTerms();
  const { palette } = useClinicTheme();

  // Local alert
  const [localAlert, setLocalAlert] = useState({ open: false, severity: "success", message: "" });
  const alertTimerRef = useRef(null);
  const showLocal = useCallback((message, severity = "success", duration = 3500) => {
    if (alertTimerRef.current) clearTimeout(alertTimerRef.current);
    setLocalAlert({ open: true, severity, message });
    alertTimerRef.current = setTimeout(() => {
      setLocalAlert((a) => ({ ...a, open: false }));
      alertTimerRef.current = null;
    }, duration);
  }, []);
  const hideLocal = useCallback(() => {
    if (alertTimerRef.current) {
      clearTimeout(alertTimerRef.current);
      alertTimerRef.current = null;
    }
    setLocalAlert((a) => ({ ...a, open: false }));
  }, []);
  useEffect(() => () => { if (alertTimerRef.current) clearTimeout(alertTimerRef.current); }, []);
  const showSuccess = useCallback((m) => showLocal(m, "success"), [showLocal]);
  const showError = useCallback((m) => showLocal(m, "error", 5000), [showLocal]);

  const [listSettings] = useListNotificationSettingsByPatientMutation();
  const [listLogs] = useListNotificationLogsByPatientMutation();
  const [deleteSetting] = useDeleteNotificationSettingMutation();
  const [sendNow] = useSendNotificationNowMutation();

  const [settings, setSettings] = useState([]);
  const [logs, setLogs] = useState([]);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);

  useEffect(() => {
    if (mode === "session") {
      setSettings(Array.isArray(initialData?.settings) ? initialData.settings : []);
      setLogs(Array.isArray(initialData?.logs) ? initialData.logs : []);
      setInitialLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, initialData?.settings, initialData?.logs]);

  const refresh = useCallback(async () => {
    if (mode !== "standalone" || !token || !patientId) return;
    try {
      const [sRes, lRes] = await Promise.all([
        listSettings({ token, patientId }).unwrap(),
        listLogs({ token, patientId }).unwrap(),
      ]);
      setSettings(sRes?.data?.list || []);
      setLogs(lRes?.data?.list || []);
    } catch (_) {
      showError("Bildirimler yüklenemedi.");
    } finally {
      setInitialLoaded(true);
    }
  }, [mode, token, patientId, listSettings, listLogs, showError]);

  useEffect(() => {
    if (mode === "standalone") refresh();
  }, [mode, refresh]);

  const selected = useMemo(
    () => settings.find((r) => r._id === selectedId) || null,
    [settings, selectedId]
  );

  const handleSelect = (id) => {
    setCreatingNew(false);
    setSelectedId(id);
  };

  const prevSelectedRef = useRef(null);
  const handleNew = () => {
    if (!canEdit) return;
    prevSelectedRef.current = selectedId;
    setSelectedId(null);
    setCreatingNew(true);
  };

  const handleCancelNew = () => {
    setCreatingNew(false);
    setSelectedId(prevSelectedRef.current);
  };

  const handleCreated = (created) => {
    setSettings((prev) => [created, ...prev]);
    setSelectedId(created._id);
    setCreatingNew(false);
    showSuccess("Bildirim ayarı oluşturuldu.");
  };

  const handleSaved = (updated) => {
    if (!updated || !updated._id) return;
    setSettings((prev) => prev.map((r) => (r._id === updated._id ? updated : r)));
    showSuccess("Bildirim ayarı güncellendi.");
  };

  const handleDelete = (rec) => {
    if (!rec?._id) return;
    setPendingDelete(rec);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!pendingDelete?._id) return;
    setDeleteLoading(true);
    try {
      await deleteSetting({
        token,
        patientId,
        notificationSettingId: pendingDelete._id,
      }).unwrap();
      setSettings((prev) => prev.filter((r) => r._id !== pendingDelete._id));
      if (selectedId === pendingDelete._id) setSelectedId(null);
      showSuccess("Bildirim ayarı silindi.");
    } catch (_) {
      showError("Bildirim ayarı silinemedi.");
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmOpen(false);
      setPendingDelete(null);
    }
  };

  const handleSendNow = async (rec) => {
    if (!rec?._id) return;
    try {
      await sendNow({
        token,
        patientId,
        notificationSettingId: rec._id,
      }).unwrap();
      showSuccess("Bildirim gönderildi.");
      await refresh();
    } catch (_) {
      showError("Bildirim gönderilemedi.");
    }
  };

  const handleEditorError = useCallback(
    () => showError("Bildirim ayarı kaydedilemedi."),
    [showError]
  );

  const headerAction = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <ActionButton
        variant="edit"
        tone="outline"
        compact
        icon={<HistoryIcon />}
        label={`Gönderim geçmişi${logs.length ? ` · ${logs.length}` : ""}`}
        onClick={() => setLogsOpen(true)}
      />
      {canEdit && !creatingNew ? (
        <ActionButton
          variant="add"
          compact
          palette={palette}
          onClick={handleNew}
          aria-label="Yeni bildirim oluştur"
          label="Yeni Bildirim"
        />
      ) : null}
    </Box>
  );

  const showEditor = creatingNew || Boolean(selected);
  const selectedLogs = selected
    ? logs.filter((l) => String(l.notificationSetting?._id || l.notificationSetting || "") === String(selected._id))
    : [];

  return (
    <Box sx={WORKSPACE_ROOT_SX}>
      <WorkspaceBody
        title="Uygulama Bildirimleri"
        count={settings.length}
        action={headerAction}
        alert={localAlert}
        onAlertClose={hideLocal}
      >
        {!initialLoaded ? (
          <WorkspaceSkeleton />
        ) : settings.length === 0 && !creatingNew ? (
          <WorkspaceEmpty
            icon={<NotificationsIcon />}
            title="Henüz bildirim ayarı yok"
            lead={`${terms.patientGen} telefonuna düzenli hatırlatma ya da soru göndermek için`}
            linkLabel="bildirim oluşturun"
            tail="Saat, sıklık ve cevap tipi her bildirimde ayrı belirlenir."
            readOnlyText={`Bu ${terms.patientLower} için planlanmış bildirim bulunmuyor.`}
            onCreate={canEdit ? handleNew : undefined}
            palette={palette}
          />
        ) : (
          <WorkspaceSplit
            list={
              <Stack divider={<Box sx={{ borderBottom: `1px solid ${COLORS.slate100}` }} />}>
                {creatingNew && (
                  <CreatingRow
                    label="Yeni bildirim oluşturuluyor…"
                    hint="Konu yazıp Kaydet'e basın"
                    onCancel={handleCancelNew}
                    palette={palette}
                  />
                )}
                {settings.map((item) => (
                  <SelectableListRow
                    key={item._id}
                    active={item._id === selectedId}
                    onClick={() => handleSelect(item._id)}
                    sx={LIST_ROW_SX}
                    title={item.subject || "Konu yok"}
                    subtitle={
                      <>
                        <RowLine flex sx={{ mt: 0.25 }}>
                          <RowChip
                            label={FREQUENCY_LABELS[item.sendFrequency] || item.sendFrequency || "—"}
                            sx={{
                              fontWeight: 600,
                              bgcolor: alpha(palette?.primary || COLORS.slate700, 0.08),
                              color: palette?.primary || COLORS.slate700,
                            }}
                          />
                          {item.responseFormat && item.responseFormat !== "none" ? (
                            <Box component="span" sx={{ fontSize: "0.75rem", color: COLORS.slate500 }}>
                              {RESPONSE_LABELS[item.responseFormat] || ""}
                            </Box>
                          ) : null}
                        </RowLine>
                        {item.message && (
                          <RowLine sx={{ mt: 0.25, fontSize: "0.78rem" }}>{item.message}</RowLine>
                        )}
                      </>
                    }
                    trailing={<RowMetaText>{item.sendTime || "—"}</RowMetaText>}
                  />
                ))}
              </Stack>
            }
            editor={
              !showEditor ? (
                <SelectPrompt
                  icon={<NotificationsIcon />}
                  title="Bir bildirim seçin"
                  description="Ayarlarını ve gönderim geçmişini görmek için soldaki listeden bir bildirim seçin."
                />
              ) : (
                <NotificationEditor
                  key={selected?._id || "new"}
                  record={selected}
                  logs={selectedLogs}
                  patientId={patientId}
                  token={token}
                  canEdit={canEdit}
                  onCreated={handleCreated}
                  onSaved={handleSaved}
                  onDelete={handleDelete}
                  onSendNow={handleSendNow}
                  onError={handleEditorError}
                />
              )
            }
          />
        )}
      </WorkspaceBody>

      <NotificationLogDrawer open={logsOpen} onClose={() => setLogsOpen(false)} logs={logs} />

      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => {
          if (deleteLoading) return;
          setDeleteConfirmOpen(false);
          setPendingDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Bildirim ayarını sil"
        message={
          pendingDelete?.subject
            ? `"${pendingDelete.subject}" adlı bildirim ayarı silinecek. Devam edilsin mi?`
            : "Bu bildirim ayarı silinecek. Devam edilsin mi?"
        }
        confirmText="Sil"
        cancelText="Vazgeç"
        confirmVariant="error"
        loading={deleteLoading}
      />
    </Box>
  );
}
